import { Button, Card, Col, Collapse, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useHistory } from 'react-router-dom';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DashboardButton from 'components/global/DashboardButton';
import api from 'api/api';
import SimpleTable from 'components/global/SimpleTable';
import dateOptions from 'components/global/Constant/dateOptions';
import {
  loanTrackingEventTypeDisplay,
  loanTrackingEventStatusDisplay,
} from 'components/global/Constant/constants';
import { enumCaseToTabCase } from 'libs/string';
import {
  addIcon,
  caretDownIcon,
  caretRightIcon,
  dashboardIcon,
  reportingIcon,
  programsIcon,
  portfolioIcon,
  lenderIcon,
  vcIcon,
  businessIcon,
  transactionIcon,
  fundIcon,
} from 'components/global/Constant/Icons';
import { shouldHaveFullEdoAccess, hasEdoAccessRestrictions } from 'libs/edoPermissions';
import { getConfig } from 'config/config';
import userAccountSignal from 'signals/UserAccount.signal';
import $appSettings from 'signals/AppSettings.signal';
import {
  STAFF_EXCLUDED,
  EDO_FUNDS_VC_HIDDEN,
  EDO_BATCH_PROCESSING_NAV_FEATURE,
} from 'components/global/Constant/componentProps';
import { isEdoFeatureExcludeVisible } from 'libs/edoNavFeatureVisibility';
import { getStatusStyles } from 'libs/functions/global.functions';
import Loader from 'components/global/Loader';
import LoanProcessingBatchesSection from './LoanProcessingBatchesSection';
import DashboardTablePagination from './DashboardTablePagination';
import { ReorderDragHandle, setReorderDragOverEffect } from 'components/global/ReorderDragHandle';

const OVERVIEW_ITEMS = [
  {
    id: 'portfolio',
    img: portfolioIcon,
    title: 'Applications',
    description: 'View and manage applications.',
    route: '/edo/portfolio?sort=id-desc',
    featureExclude: {},
    requiresProgramAccess: true,
  },
  {
    id: 'programs',
    img: programsIcon,
    title: 'Programs',
    description: 'Manage funding programs and their configurations.',
    route: '/edo/programs',
    featureExclude: STAFF_EXCLUDED,
    requiresProgramAccess: true,
  },
  {
    id: 'lenders',
    img: lenderIcon,
    title: 'Lenders',
    description: 'View and manage lender organizations.',
    route: '/edo/lenders?sort=id-desc',
    featureExclude: {},
    requiresLenderAccess: true,
  },
  {
    id: 'vc',
    img: vcIcon,
    title: 'VCs',
    description: 'View and manage venture capital firms.',
    route: '/edo/vc?sort=id-desc',
    featureExclude: EDO_FUNDS_VC_HIDDEN,
    requiresProgramAccess: true,
  },
  {
    id: 'funds',
    img: fundIcon,
    title: 'Funds',
    description: 'View and manage funds.',
    route: '/edo/funds',
    featureExclude: EDO_FUNDS_VC_HIDDEN,
    requiresProgramAccess: true,
  },
  {
    id: 'business',
    img: businessIcon,
    title: 'Businesses',
    description: 'Search and view businesses.',
    route: '/edo/business',
    featureExclude: {},
  },
  {
    id: 'performance',
    img: dashboardIcon,
    title: 'Performance',
    description: 'Monitor program performance metrics.',
    route: '/edo/performance',
    featureExclude: {},
    requiresProgramAccess: true,
  },
  {
    id: 'transactions',
    img: transactionIcon,
    title: 'Transactions',
    description: 'View transaction history and metrics.',
    route: '/edo/transactions',
    featureExclude: {},
  },
  {
    id: 'reporting',
    img: reportingIcon,
    title: 'Reporting',
    description: 'Access treasury and adhoc reports.',
    route: '/edo/reporting',
    featureExclude: {},
  },
  {
    id: 'add-program',
    img: addIcon,
    title: 'Add New Program',
    description: 'Create and manage new programs.',
    route: '/edo/programs/add-program',
    featureExclude: STAFF_EXCLUDED,
  },
];

const getVisibleOverviewItems = (userData) => {
  const fullAccess = shouldHaveFullEdoAccess(userData);
  const hasRestrictions = hasEdoAccessRestrictions(userData);
  const role = userData?.role;
  const appConfig = getConfig('APP_CONFIG');
  const appId = appConfig?.appId;

  const hasAdminOrManagerProgramAccess = (userData?.edoUserProgramAccesses || []).some(
    (a) => a.role === 'ADMIN' || a.role === 'MANAGER'
  );

  return OVERVIEW_ITEMS.filter((item) => {
    if (role === 'STAFF' && (item.id === 'programs' || item.id === 'add-program')) {
      if (!hasAdminOrManagerProgramAccess) return false;
    }

    if (appId && role && item.featureExclude[appId]?.[role] === true) {
      if ((item.id === 'programs' || item.id === 'add-program') && role === 'STAFF') {
        if (hasAdminOrManagerProgramAccess) return true;
      }
      return false;
    }

    if (!fullAccess) {
      if (item.requiresProgramAccess && hasRestrictions) {
        const hasProgramAccess = (userData?.edoUserProgramAccesses || []).length > 0;
        if (!hasProgramAccess) return false;
      }
      if (item.requiresLenderAccess && hasRestrictions) {
        const hasLenderAccess = (userData?.edoUserLenderAccesses || []).length > 0;
        if (!hasLenderAccess) return false;
      }
    }

    return true;
  });
};

const PROGRAM_ACCESS_ROLES = { admin: 'ADMIN', analyst: 'ANALYST', manager: 'MANAGER' };
const USER_ACCOUNT_ROLES = { admin: 'ADMIN', staff: 'STAFF', owner: 'OWNER', superAdmin: 'SUPER_ADMIN' };

const hideEdoDashboardAppProgramSections = (userData) => {
  if (!userData || userData.role !== USER_ACCOUNT_ROLES.staff) return false;
  const accesses = userData.edoUserProgramAccesses || [];
  if (accesses.length === 0) return false;
  const roles = accesses.map((a) => a.role).filter(Boolean);
  if (roles.includes(PROGRAM_ACCESS_ROLES.admin)) return false;
  if (roles.includes(PROGRAM_ACCESS_ROLES.manager)) return false;
  return true;
};

const PAGE_SIZE = 5;

const EMPTY_FILTER_LENDERS = [];

const DASHBOARD_CARD_COLLAPSE_MS = 400;

const DASHBOARD_SECTION_IDS = ['loanTracking', 'applications', 'programRequests'];
const DASHBOARD_SECTION_DRAG_TYPE = 'application/x-edo-dash-section';

const MAIN_DASHBOARD_SECTION_IDS = ['trackingRequests', 'loanProcessingBatches', 'quickAccess'];
const MAIN_DASHBOARD_DRAG_TYPE = 'application/x-edo-dash-main-section';

const MAIN_BLOCK_PREF_KEYS = {
  trackingRequests: 'trackingRequestsExpanded',
  loanProcessingBatches: 'loanProcessingBatchesExpanded',
  quickAccess: 'quickAccessExpanded',
};

const readMainBlockExpandedFromPrefs = (prefs) => ({
  trackingRequests: prefs.trackingRequestsExpanded !== false,
  loanProcessingBatches: prefs.loanProcessingBatchesExpanded !== false,
  quickAccess: prefs.quickAccessExpanded !== false,
});

const LOAN_BATCH_SECTION_IDS = ['management_l1', 'management_l2', 'outgoing', 'completed'];
const LOAN_BATCH_SECTION_DRAG_TYPE = 'application/x-edo-dash-loan-batch-section';

const QUICK_ACCESS_DRAG_TYPE = 'application/x-edo-dash-quick-access';

const normalizeQuickAccessOrder = (saved, defaultOrderedVisibleIds) => {
  if (!Array.isArray(defaultOrderedVisibleIds) || defaultOrderedVisibleIds.length === 0) {
    return [];
  }
  const visibleSet = new Set(defaultOrderedVisibleIds);
  const fromSaved =
    Array.isArray(saved) && saved.length > 0 ? saved.filter((id) => visibleSet.has(id)) : [];
  const seen = new Set(fromSaved);
  for (const id of defaultOrderedVisibleIds) {
    if (!seen.has(id)) {
      fromSaved.push(id);
      seen.add(id);
    }
  }
  return fromSaved;
};

const normalizeSectionOrder = (raw, hideApps) => {
  if (hideApps) return ['loanTracking'];
  if (!Array.isArray(raw) || raw.length !== DASHBOARD_SECTION_IDS.length) {
    return [...DASHBOARD_SECTION_IDS];
  }
  const set = new Set(raw);
  if (set.size !== DASHBOARD_SECTION_IDS.length) return [...DASHBOARD_SECTION_IDS];
  if (!raw.every((id) => DASHBOARD_SECTION_IDS.includes(id))) return [...DASHBOARD_SECTION_IDS];
  return [...raw];
};

const reorderSections = (order, sourceId, targetId) => {
  if (sourceId === targetId) return order;
  const next = [...order];
  const si = next.indexOf(sourceId);
  const ti = next.indexOf(targetId);
  if (si < 0 || ti < 0) return order;
  const [item] = next.splice(si, 1);
  next.splice(ti, 0, item);
  return next;
};

const normalizeMainSectionOrder = (raw, showLoanBatches) => {
  const allowed = showLoanBatches
    ? [...MAIN_DASHBOARD_SECTION_IDS]
    : MAIN_DASHBOARD_SECTION_IDS.filter((id) => id !== 'loanProcessingBatches');
  if (!Array.isArray(raw)) return [...allowed];
  const filtered = raw.filter((id) => allowed.includes(id));
  const seen = new Set();
  const deduped = filtered.filter((id) => {
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  for (const id of allowed) {
    if (!deduped.includes(id)) deduped.push(id);
  }
  if (deduped.length !== allowed.length) return [...allowed];
  return deduped;
};

/** When loan section is hidden, merge visible reorder into full 3-slot order (loan stays at prior index). */
const mergeMainOrderAfterVisibleReorder = (fullOrder, newVisibleOrder, showLoanBatches) => {
  if (showLoanBatches) return newVisibleOrder;
  const loanId = 'loanProcessingBatches';
  const prevIdx = fullOrder.indexOf(loanId);
  if (prevIdx < 0) return normalizeMainSectionOrder(newVisibleOrder, false);
  const next = [...newVisibleOrder];
  next.splice(prevIdx, 0, loanId);
  return normalizeMainSectionOrder(next, true);
};

const normalizeLoanBatchSectionOrder = (raw) => {
  if (!Array.isArray(raw) || raw.length !== LOAN_BATCH_SECTION_IDS.length) {
    return [...LOAN_BATCH_SECTION_IDS];
  }
  const set = new Set(raw);
  if (set.size !== LOAN_BATCH_SECTION_IDS.length) return [...LOAN_BATCH_SECTION_IDS];
  if (!raw.every((id) => LOAN_BATCH_SECTION_IDS.includes(id))) return [...LOAN_BATCH_SECTION_IDS];
  return [...raw];
};

const LOAN_BATCH_QUEUE_META = {
  management_l1: {
    title: 'Management Queue — Level 1 (Approve → Sent to Ex-Director)',
    queue: 'management_l1',
    emptyText: 'No batches awaiting Level 1 approval.',
    preferenceKey: 'managementL1Expanded',
  },
  management_l2: {
    title: 'Management Queue — Level 2 (Funding Memo → Approve → Sent to Admin)',
    queue: 'management_l2',
    emptyText: 'No batches awaiting Level 2 approval.',
    preferenceKey: 'managementL2Expanded',
  },
  outgoing: {
    title: 'Outgoing Assignments',
    queue: 'outgoing',
    emptyText: 'No batches in outgoing workflow.',
    preferenceKey: 'outgoingExpanded',
  },
  completed: {
    title: 'Completed Batches',
    status: 'COMPLETED',
    emptyText: 'No completed batches yet.',
    preferenceKey: 'completedExpanded',
  },
};

const LOAN_TRACKING_HEADERS = [
  { key: 'lenderLoanNumber', title: 'Lender Loan #', sortable: true },
  { key: 'businessName', title: 'Business Name', sortable: true },
  { key: 'programName', title: 'Program Name', sortable: true },
  { key: 'eventType', title: 'Event Type', sortable: true },
  { key: 'status', title: 'Event Status', sortable: true },
  { key: 'action', title: 'Action' },
];

const PROGRAM_REQUEST_HEADERS = [
  { key: 'id', title: 'ID', sortable: true },
  { key: 'programName', title: 'Program Name', sortable: true },
  { key: 'organization', title: 'Organization', sortable: true },
  { key: 'createdDate', title: 'Date Submitted', sortable: true },
  { key: 'action', title: 'Action' },
];

const formatApplicationStatus = (status) =>
  status === 'IN_PROGRESS' ? 'Draft' : enumCaseToTabCase(status);

const APPLICATION_HEADERS = [
  { key: 'loanStatus', title: 'Status', sortable: true },
  { key: 'createdDate', title: 'Date Submitted', sortable: true },
  { key: 'businessName', title: 'Business Name', sortable: true },
  { key: 'programName', title: 'Program Name', sortable: true },
  { key: 'lenderLoanNumber', title: 'Lender Loan #', sortable: true },
  { key: 'eventType', title: 'Event Type' },
  { key: 'status', title: 'Event Status' },
  { key: 'action', title: 'Action' },
];

const sortToParam = (s) => (s?.key ? `${s.key}-${s.dir}` : undefined);

const appendParam = (params, key, value) => {
  if (value !== undefined && value !== null && value !== '') {
    params.set(key, String(value));
  }
};

function DashboardTableSection({
  title,
  filterLenders,
  lenderValue,
  onLenderChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  searchValue,
  onSearchChange,
  searchDisabled,
  onClearFilters,
  idPrefix,
  children,
  defaultExpanded = true,
  savedExpanded,
  persistExpanded = false,
  preferenceKey,
  preferencesSyncRef,
  headerDragHandle,
  pageSizeSelect,
}) {
  const initialExpanded =
    typeof savedExpanded === 'boolean' ? savedExpanded : defaultExpanded;
  const [expanded, setExpanded] = useState(initialExpanded);
  const collapseId = `${idPrefix}-section-body`;
  const headingId = `${idPrefix}-collapse-heading`;

  useEffect(() => {
    if (typeof savedExpanded === 'boolean') {
      setExpanded(savedExpanded);
    }
  }, [savedExpanded]);

  const toggleExpanded = useCallback(async () => {
    const next = !expanded;
    setExpanded(next);
    if (!persistExpanded || !preferenceKey) return;
    try {
      const res = await api.patch({
        path: '/users/current/preferences',
        body: {
          edoDashboard: {
            trackingRequestsCards: { [preferenceKey]: next },
          },
        },
      });
      const userPreferences = res?.userPreferences;
      if (userPreferences && preferencesSyncRef) {
        preferencesSyncRef.current = userPreferences;
      }
    } catch (err) {
      setExpanded(!next);
      console.error('Failed to save dashboard card preference', err);
    }
  }, [expanded, persistExpanded, preferenceKey]);

  const onHeaderKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <Card className="edo-dashboard-nested-card border border-gray rounded-0">
      <Card.Header className="edo-dashboard-section-header border-0 rounded-0 px-16 py-8 d-flex align-items-center">
        <div className="d-flex align-items-center flex-grow-1" style={{ minWidth: 0 }}>
          {headerDragHandle ? (
            <span
              className="flex-shrink-0 d-inline-flex align-items-center align-self-center"
              onClick={(e) => e.stopPropagation()}
            >
              {headerDragHandle}
            </span>
          ) : null}
          <div
            id={headingId}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-controls={collapseId}
            className="flex-grow-1 d-flex align-items-center justify-content-between"
            style={{ minWidth: 0, cursor: 'pointer' }}
            onClick={toggleExpanded}
            onKeyDown={onHeaderKeyDown}
            title={expanded ? 'Collapse section' : 'Expand section'}
          >
            <div className="flex-grow-1 pr-8" style={{ minWidth: 0 }}>
              {title}
            </div>
            <span className="flex-shrink-0 p-8" aria-hidden="true">
              <FontAwesomeIcon
                icon={expanded ? caretDownIcon : caretRightIcon}
                className="align-middle edo-dashboard-section-header__chevron"
              />
            </span>
          </div>
        </div>
      </Card.Header>
      <Collapse in={expanded} appear={false} timeout={DASHBOARD_CARD_COLLAPSE_MS}>
        <div id={collapseId}>
          <div className="edo-dashboard-card-filter px-16 py-16">
            <Form className="mb-0">
              <div className="d-flex flex-column flex-lg-row flex-lg-nowrap align-items-stretch align-items-lg-center">
                <div className="d-flex flex-wrap align-items-center flex-grow-1 mr-lg-24 mb-16 mb-lg-0">
                  <Form.Group controlId={`${idPrefix}-lender`} className="mb-12 mb-lg-0 mr-0 mr-lg-24">
                    <Form.Control
                      as="select"
                      size="sm"
                      style={{ minWidth: '11rem' }}
                      value={lenderValue}
                      onChange={(e) => onLenderChange(e.target.value)}
                      aria-label="Lender"
                    >
                      <option value="">All Lenders</option>
                      {filterLenders.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <Form.Group className="mb-12 mb-lg-0 mr-0 mr-lg-24">
                    <div className="d-flex flex-nowrap align-items-center overflow-auto">
                      <Form.Control
                        id={`${idPrefix}-date-from`}
                        type="date"
                        size="sm"
                        className="flex-shrink-0"
                        style={{ width: '10.5rem', maxWidth: '10.5rem' }}
                        value={dateFrom}
                        onChange={(e) => onDateFromChange(e.target.value)}
                        aria-label="Created start date"
                      />
                      <span className="small text-muted flex-shrink-0 mx-8" aria-hidden="true">
                        to
                      </span>
                      <Form.Control
                        id={`${idPrefix}-date-to`}
                        type="date"
                        size="sm"
                        className="flex-shrink-0"
                        style={{ width: '10.5rem', maxWidth: '10.5rem' }}
                        value={dateTo}
                        onChange={(e) => onDateToChange(e.target.value)}
                        aria-label="Created end date"
                      />
                    </div>
                  </Form.Group>
                  <Form.Group
                    controlId={`${idPrefix}-search`}
                    className="mb-0 mb-12 mb-lg-0 ml-lg-auto edo-dashboard-filter-search"
                    style={{ minWidth: '12rem', maxWidth: '22rem' }}
                  >
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="Search keywords…"
                      value={searchValue}
                      disabled={searchDisabled}
                      onChange={(e) => onSearchChange(e.target.value)}
                      aria-label="Filter this page"
                    />
                  </Form.Group>
                  {pageSizeSelect ? (
                    <Form.Group
                      controlId={`${idPrefix}-page-size`}
                      className="mb-0 mb-12 mb-lg-0 ml-lg-16 edo-dashboard-filter-page-size"
                    >
                      <Form.Control
                        as="select"
                        size="sm"
                        style={{ minWidth: '7.5rem' }}
                        value={String(pageSizeSelect.value)}
                        onChange={(e) => pageSizeSelect.onChange(Number(e.target.value))}
                        aria-label={pageSizeSelect.ariaLabel || 'Rows per page'}
                      >
                        {pageSizeSelect.options.map((n) => (
                          <option key={n} value={n}>
                            {n} per page
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  ) : null}
                </div>
                <div className="d-flex justify-content-start justify-content-lg-end align-items-center flex-shrink-0">
                  <Button variant="outline-secondary" size="sm" type="button" onClick={onClearFilters}>
                    Clear filters
                  </Button>
                </div>
              </div>
            </Form>
          </div>
          <Card.Body className="px-16 py-16 edo-dashboard-nested-section-body">{children}</Card.Body>
        </div>
      </Collapse>
    </Card>
  );
}

const MemoDashboardTableSection = memo(DashboardTableSection);

const EdoMainSectionCard = memo(function EdoMainSectionCard({
  sectionDragId,
  title,
  expanded,
  onToggle,
  canReorderMain,
  bodyClassName = 'px-16 py-16',
  children,
}) {
  const collapseId = `edo-main-${sectionDragId}-collapse`;
  const headingId = `edo-main-${sectionDragId}-heading`;

  const onHeaderKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <Card className="border border-gray edo-dashboard-main-card">
      <div className="edo-dashboard-main-card-header">
        <div className="d-flex align-items-center px-16 py-12 flex-grow-1" style={{ minWidth: 0 }}>
          <span
            className="flex-shrink-0 mr-8 d-inline-flex align-items-center align-self-center"
            onClick={(e) => e.stopPropagation()}
          >
            <ReorderDragHandle
              itemId={sectionDragId}
              enabled={canReorderMain}
              dataTransferType={MAIN_DASHBOARD_DRAG_TYPE}
              variant="light"
              ariaLabel="Drag to reorder section"
            />
          </span>
          <div
            id={headingId}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-controls={collapseId}
            className="flex-grow-1 d-flex align-items-center justify-content-between"
            style={{ minWidth: 0, cursor: 'pointer' }}
            onClick={onToggle}
            onKeyDown={onHeaderKeyDown}
            title={expanded ? 'Collapse section' : 'Expand section'}
          >
            <h5 className="mt-0 mb-0 edo-dashboard-main-card-title">{title}</h5>
            <span className="flex-shrink-0 p-8" aria-hidden="true">
              <FontAwesomeIcon
                icon={expanded ? caretDownIcon : caretRightIcon}
                className="align-middle text-white"
              />
            </span>
          </div>
        </div>
      </div>
      <Collapse in={expanded} appear={false} timeout={DASHBOARD_CARD_COLLAPSE_MS}>
        <div id={collapseId}>
          <Card.Body className={bodyClassName}>{children}</Card.Body>
        </div>
      </Collapse>
    </Card>
  );
});

function programRequestToRows(list) {
  return list.map((pr) => ({
    id: pr.id,
    programName: pr.fundingProgram?.name || `Program #${pr.fundingProgramId}`,
    organization: pr.lender?.name || pr.ventureCapitalFirm?.name || '—',
    createdDate: pr.createdDate
      ? new Date(pr.createdDate).toLocaleString('en-US', dateOptions.date)
      : '—',
    action: ({ headers, colIndex }) => (
      <td key={headers[colIndex]?.key} className="text-right">
        <Link to={`/edo/program-requests/${pr.id}`} className="btn btn-sm btn-outline-dark rounded">
          View
        </Link>
      </td>
    ),
  }));
}

function applicationToRows(list) {
  return list.map((app) => {
    const event = app.events?.[0];
    return {
      id: app.id,
      businessName: app.business?.name || `Application #${app.id}`,
      programName: app.fundingProgram?.name || '—',
      lenderLoanNumber: app.lenderLoanNumber != null && app.lenderLoanNumber !== '' ? app.lenderLoanNumber : '—',
      createdDate: (app.receivedDate ?? app.createdDate)
        ? new Date(app.receivedDate ?? app.createdDate).toLocaleString('en-US', dateOptions.date)
        : '—',
      loanStatusDisplay: app.status ? formatApplicationStatus(app.status) : '—',
      loanStatus: ({ headers, colIndex }) => (
        <td
          key={headers[colIndex]?.key}
          className={
            app.status
              ? `${getStatusStyles(app.status).class} align-middle`
              : 'align-middle'
          }
        >
          {app.status ? formatApplicationStatus(app.status) : '—'}
        </td>
      ),
      eventType: event?.eventCode
        ? loanTrackingEventTypeDisplay(event.eventCode, event.status)
        : '—',
      status: event?.status
        ? loanTrackingEventStatusDisplay(event.eventCode, event.status)
        : '—',
      action: ({ headers, colIndex }) => (
        <td key={headers[colIndex]?.key} className="text-right">
          <Link to={`/edo/portfolio/${app.id}?tab=loanTracking`} className="btn btn-sm btn-outline-dark rounded">
            View
          </Link>
        </td>
      ),
    };
  });
}

const QuickAccessGrid = memo(function QuickAccessGrid({
  items,
  headingAddon,
  canReorderQuickAccess,
  onQuickAccessDragOver,
  onQuickAccessDrop,
  suppressHeading = false,
}) {
  return (
    <>
      {!suppressHeading ? (
        <div className="d-flex align-items-center mb-16">
          {headingAddon ? (
            <span
              className="flex-shrink-0 d-inline-flex align-items-center mr-8"
              onClick={(e) => e.stopPropagation()}
            >
              {headingAddon}
            </span>
          ) : null}
          <h5 className="mt-0 mb-0 text-primary">Quick Access</h5>
        </div>
      ) : null}
      <Row>
        {items.map((item) => (
          <Col
            key={item.id}
            sm={6}
            md={4}
            lg={3}
            xl={2}
            className="mb-16 position-relative"
            onDragOver={canReorderQuickAccess ? onQuickAccessDragOver : undefined}
            onDrop={canReorderQuickAccess ? (ev) => onQuickAccessDrop(ev, item.id) : undefined}
          >
            <div className="position-relative edo-dashboard-quick-access-tile">
              <Link to={item.route} className="text-decoration-none text-secondary d-block">
                <DashboardButton
                  title={item.title}
                  description={item.description}
                  faIcon={item.img}
                  compact
                  className="rounded-0"
                />
              </Link>
              {canReorderQuickAccess ? (
                <span
                  className="position-absolute d-inline-flex align-items-center justify-content-center"
                  style={{ top: 6, left: 6, zIndex: 2 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ReorderDragHandle
                    itemId={item.id}
                    enabled
                    dataTransferType={QUICK_ACCESS_DRAG_TYPE}
                    variant="secondary"
                    ariaLabel="Drag to reorder section"
                  />
                </span>
              ) : null}
            </div>
          </Col>
        ))}
      </Row>
    </>
  );
});

const EdoDashboardOverview = () => {
  const history = useHistory();
  const cardPreferencesSyncRef = useRef(null);

  // PATCH responses are stored in the ref and merged into userAccountSignal on unmount only, so toggling cards does not re-render the whole overview.
  useEffect(
    () => () => {
      const userPreferences = cardPreferencesSyncRef.current;
      if (!userPreferences) return;
      const prev = userAccountSignal.value;
      if (!prev?.userData) return;
      userAccountSignal.update({
        userData: {
          ...prev.userData,
          userPreferences,
        },
      });
      cardPreferencesSyncRef.current = null;
    },
    []
  );

  const dashboardUserData = userAccountSignal.value?.userData;
  const visibleQuickAccessItems = useMemo(
    () => getVisibleOverviewItems(dashboardUserData),
    [
      dashboardUserData?.id,
      dashboardUserData?.role,
      dashboardUserData?.edoUserProgramAccesses,
      dashboardUserData?.edoUserLenderAccesses,
    ]
  );
  const hideAppProgramSections = hideEdoDashboardAppProgramSections(dashboardUserData);
  const PORTAL_TYPES = $appSettings.value?.constants?.PORTAL_TYPES;
  const persistEdoDashboardCards =
    dashboardUserData?.account?.portalType === PORTAL_TYPES?.edo;

  const showManageBatchProcessingLink = useMemo(
    () =>
      Boolean(
        dashboardUserData &&
          isEdoFeatureExcludeVisible(
            EDO_BATCH_PROCESSING_NAV_FEATURE,
            dashboardUserData
          )
      ),
    [
      dashboardUserData?.id,
      dashboardUserData?.role,
      dashboardUserData?.edoUserProgramAccesses,
    ]
  );

  const trackingCardsPrefs = useMemo(() => {
    const edoDash = dashboardUserData?.userPreferences?.edoDashboard;
    if (
      !edoDash ||
      typeof edoDash !== 'object' ||
      Array.isArray(edoDash) ||
      !edoDash.trackingRequestsCards ||
      typeof edoDash.trackingRequestsCards !== 'object' ||
      Array.isArray(edoDash.trackingRequestsCards)
    ) {
      return {};
    }
    return edoDash.trackingRequestsCards;
  }, [dashboardUserData?.id, dashboardUserData?.userPreferences]);

  const loanBatchCardsPrefs = useMemo(() => {
    const edoDash = dashboardUserData?.userPreferences?.edoDashboard;
    if (
      !edoDash ||
      typeof edoDash !== 'object' ||
      Array.isArray(edoDash) ||
      !edoDash.loanProcessingBatchCards ||
      typeof edoDash.loanProcessingBatchCards !== 'object' ||
      Array.isArray(edoDash.loanProcessingBatchCards)
    ) {
      return {};
    }
    return edoDash.loanProcessingBatchCards;
  }, [dashboardUserData?.id, dashboardUserData?.userPreferences]);

  const mainDashboardPrefs = useMemo(() => {
    const edoDash = dashboardUserData?.userPreferences?.edoDashboard;
    if (
      !edoDash ||
      typeof edoDash !== 'object' ||
      Array.isArray(edoDash) ||
      !edoDash.mainDashboardCards ||
      typeof edoDash.mainDashboardCards !== 'object' ||
      Array.isArray(edoDash.mainDashboardCards)
    ) {
      return {};
    }
    return edoDash.mainDashboardCards;
  }, [dashboardUserData?.id, dashboardUserData?.userPreferences]);

  const quickAccessCardsPrefs = useMemo(() => {
    const edoDash = dashboardUserData?.userPreferences?.edoDashboard;
    if (
      !edoDash ||
      typeof edoDash !== 'object' ||
      Array.isArray(edoDash) ||
      !edoDash.quickAccessCards ||
      typeof edoDash.quickAccessCards !== 'object' ||
      Array.isArray(edoDash.quickAccessCards)
    ) {
      return {};
    }
    return edoDash.quickAccessCards;
  }, [dashboardUserData?.id, dashboardUserData?.userPreferences]);

  const defaultQuickAccessIds = useMemo(
    () => visibleQuickAccessItems.map((i) => i.id),
    [visibleQuickAccessItems]
  );

  const defaultQuickAccessIdsKey = defaultQuickAccessIds.join(',');

  const [quickAccessOrderIds, setQuickAccessOrderIds] = useState(() =>
    normalizeQuickAccessOrder(quickAccessCardsPrefs.itemOrder, defaultQuickAccessIds)
  );

  useEffect(() => {
    setQuickAccessOrderIds(
      normalizeQuickAccessOrder(quickAccessCardsPrefs.itemOrder, defaultQuickAccessIds)
    );
  }, [quickAccessCardsPrefs.itemOrder, defaultQuickAccessIdsKey]);

  const quickAccessItemsOrdered = useMemo(() => {
    const byId = Object.fromEntries(visibleQuickAccessItems.map((i) => [i.id, i]));
    return quickAccessOrderIds.map((id) => byId[id]).filter(Boolean);
  }, [quickAccessOrderIds, visibleQuickAccessItems]);

  const showLoanBatchesSection = persistEdoDashboardCards && dashboardUserData;
  const canReorderSections = persistEdoDashboardCards && !hideAppProgramSections;
  const canReorderMainSections = persistEdoDashboardCards;
  const canReorderLoanBatchSections = showLoanBatchesSection;
  const canReorderQuickAccess =
    persistEdoDashboardCards && quickAccessItemsOrdered.length > 1;

  const [sectionOrder, setSectionOrder] = useState(() =>
    normalizeSectionOrder(trackingCardsPrefs.sectionOrder, hideAppProgramSections)
  );

  useEffect(() => {
    setSectionOrder(normalizeSectionOrder(trackingCardsPrefs.sectionOrder, hideAppProgramSections));
  }, [hideAppProgramSections, trackingCardsPrefs.sectionOrder]);

  const handleSectionDragOver = useCallback(
    (e) => {
      setReorderDragOverEffect(e, canReorderSections);
    },
    [canReorderSections]
  );

  const handleSectionDrop = useCallback(
    async (e, targetId) => {
      if (!canReorderSections) return;
      e.preventDefault();
      const sourceId = e.dataTransfer.getData(DASHBOARD_SECTION_DRAG_TYPE);
      if (!sourceId || sourceId === targetId) return;
      const prev = sectionOrder;
      const next = reorderSections(prev, sourceId, targetId);
      if (next.every((id, i) => id === prev[i])) return;
      setSectionOrder(next);
      try {
        const res = await api.patch({
          path: '/users/current/preferences',
          body: {
            edoDashboard: {
              trackingRequestsCards: { sectionOrder: next },
            },
          },
        });
        const userPreferences = res?.userPreferences;
        if (userPreferences && cardPreferencesSyncRef) {
          cardPreferencesSyncRef.current = userPreferences;
        }
      } catch (err) {
        setSectionOrder(prev);
        console.error('Failed to save dashboard section order', err);
      }
    },
    [canReorderSections, sectionOrder]
  );

  const [mainSectionOrderFull, setMainSectionOrderFull] = useState(() =>
    normalizeMainSectionOrder(mainDashboardPrefs.sectionOrder, true)
  );

  useEffect(() => {
    setMainSectionOrderFull(normalizeMainSectionOrder(mainDashboardPrefs.sectionOrder, true));
  }, [mainDashboardPrefs.sectionOrder]);

  const mainSectionOrderDisplay = useMemo(
    () => normalizeMainSectionOrder(mainSectionOrderFull, showLoanBatchesSection),
    [mainSectionOrderFull, showLoanBatchesSection]
  );

  const [mainBlockExpanded, setMainBlockExpanded] = useState(() =>
    readMainBlockExpandedFromPrefs(mainDashboardPrefs)
  );

  useEffect(() => {
    setMainBlockExpanded(readMainBlockExpandedFromPrefs(mainDashboardPrefs));
  }, [
    mainDashboardPrefs.trackingRequestsExpanded,
    mainDashboardPrefs.loanProcessingBatchesExpanded,
    mainDashboardPrefs.quickAccessExpanded,
  ]);

  const toggleMainBlockExpanded = useCallback(
    async (blockId) => {
      const prefKey = MAIN_BLOCK_PREF_KEYS[blockId];
      const prev = mainBlockExpanded[blockId];
      const next = !prev;
      setMainBlockExpanded((s) => ({ ...s, [blockId]: next }));
      if (!persistEdoDashboardCards || !prefKey) return;
      try {
        const res = await api.patch({
          path: '/users/current/preferences',
          body: {
            edoDashboard: {
              mainDashboardCards: { [prefKey]: next },
            },
          },
        });
        const userPreferences = res?.userPreferences;
        if (userPreferences && cardPreferencesSyncRef) {
          cardPreferencesSyncRef.current = userPreferences;
        }
      } catch (err) {
        setMainBlockExpanded((s) => ({ ...s, [blockId]: prev }));
        console.error('Failed to save main dashboard section visibility', err);
      }
    },
    [persistEdoDashboardCards, mainBlockExpanded]
  );

  const handleMainSectionDragOver = useCallback(
    (e) => {
      setReorderDragOverEffect(e, canReorderMainSections);
    },
    [canReorderMainSections]
  );

  const handleMainSectionDrop = useCallback(
    async (e, targetId) => {
      if (!canReorderMainSections) return;
      e.preventDefault();
      const sourceId = e.dataTransfer.getData(MAIN_DASHBOARD_DRAG_TYPE);
      if (!sourceId || sourceId === targetId) return;
      const displayPrev = mainSectionOrderDisplay;
      const nextDisplay = reorderSections(displayPrev, sourceId, targetId);
      if (nextDisplay.every((id, i) => id === displayPrev[i])) return;
      const nextFull = showLoanBatchesSection
        ? nextDisplay
        : mergeMainOrderAfterVisibleReorder(mainSectionOrderFull, nextDisplay, false);
      const prevFull = mainSectionOrderFull;
      setMainSectionOrderFull(nextFull);
      try {
        const res = await api.patch({
          path: '/users/current/preferences',
          body: {
            edoDashboard: {
              mainDashboardCards: { sectionOrder: nextFull },
            },
          },
        });
        const userPreferences = res?.userPreferences;
        if (userPreferences && cardPreferencesSyncRef) {
          cardPreferencesSyncRef.current = userPreferences;
        }
      } catch (err) {
        setMainSectionOrderFull(prevFull);
        console.error('Failed to save main dashboard section order', err);
      }
    },
    [
      canReorderMainSections,
      mainSectionOrderDisplay,
      mainSectionOrderFull,
      showLoanBatchesSection,
    ]
  );

  const [loanBatchSectionOrder, setLoanBatchSectionOrder] = useState(() =>
    normalizeLoanBatchSectionOrder(loanBatchCardsPrefs.sectionOrder)
  );

  useEffect(() => {
    setLoanBatchSectionOrder(normalizeLoanBatchSectionOrder(loanBatchCardsPrefs.sectionOrder));
  }, [loanBatchCardsPrefs.sectionOrder]);

  const handleLoanBatchSectionDragOver = useCallback(
    (e) => {
      setReorderDragOverEffect(e, canReorderLoanBatchSections);
    },
    [canReorderLoanBatchSections]
  );

  const handleLoanBatchSectionDrop = useCallback(
    async (e, targetId) => {
      if (!canReorderLoanBatchSections) return;
      e.preventDefault();
      const sourceId = e.dataTransfer.getData(LOAN_BATCH_SECTION_DRAG_TYPE);
      if (!sourceId || sourceId === targetId) return;
      const prev = loanBatchSectionOrder;
      const next = reorderSections(prev, sourceId, targetId);
      if (next.every((id, i) => id === prev[i])) return;
      setLoanBatchSectionOrder(next);
      try {
        const res = await api.patch({
          path: '/users/current/preferences',
          body: {
            edoDashboard: {
              loanProcessingBatchCards: { sectionOrder: next },
            },
          },
        });
        const userPreferences = res?.userPreferences;
        if (userPreferences && cardPreferencesSyncRef) {
          cardPreferencesSyncRef.current = userPreferences;
        }
      } catch (err) {
        setLoanBatchSectionOrder(prev);
        console.error('Failed to save loan batch section order', err);
      }
    },
    [canReorderLoanBatchSections, loanBatchSectionOrder]
  );

  const handleQuickAccessDragOver = useCallback(
    (e) => {
      setReorderDragOverEffect(e, canReorderQuickAccess);
    },
    [canReorderQuickAccess]
  );

  const handleQuickAccessDrop = useCallback(
    async (e, targetId) => {
      if (!canReorderQuickAccess) return;
      e.preventDefault();
      const sourceId = e.dataTransfer.getData(QUICK_ACCESS_DRAG_TYPE);
      if (!sourceId || sourceId === targetId) return;
      const prev = quickAccessOrderIds;
      const next = reorderSections(prev, sourceId, targetId);
      if (next.every((id, i) => id === prev[i])) return;
      setQuickAccessOrderIds(next);
      try {
        const res = await api.patch({
          path: '/users/current/preferences',
          body: {
            edoDashboard: {
              quickAccessCards: { itemOrder: next },
            },
          },
        });
        const userPreferences = res?.userPreferences;
        if (userPreferences && cardPreferencesSyncRef) {
          cardPreferencesSyncRef.current = userPreferences;
        }
      } catch (err) {
        setQuickAccessOrderIds(prev);
        console.error('Failed to save quick access tile order', err);
      }
    },
    [canReorderQuickAccess, quickAccessOrderIds]
  );

  const [loanTrackingEvents, setLoanTrackingEvents] = useState([]);
  const [loanTrackingTotal, setLoanTrackingTotal] = useState(0);
  const [assignmentsLoaded, setAssignmentsLoaded] = useState(false);

  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoaded, setDashboardLoaded] = useState(false);

  const [ltPage, setLtPage] = useState(1);
  const [prPage, setPrPage] = useState(1);
  const [appPage, setAppPage] = useState(1);

  const [ltSort, setLtSort] = useState({ key: 'eventDate', dir: 'desc' });
  const [prSort, setPrSort] = useState({ key: 'createdDate', dir: 'desc' });
  const [appSort, setAppSort] = useState({ key: 'id', dir: 'desc' });

  const [ltLenderId, setLtLenderId] = useState('');
  const [ltDateFrom, setLtDateFrom] = useState('');
  const [ltDateTo, setLtDateTo] = useState('');
  const [prLenderId, setPrLenderId] = useState('');
  const [prDateFrom, setPrDateFrom] = useState('');
  const [prDateTo, setPrDateTo] = useState('');
  const [appLenderId, setAppLenderId] = useState('');
  const [appDateFrom, setAppDateFrom] = useState('');
  const [appDateTo, setAppDateTo] = useState('');

  const [loanTrackingSearch, setLoanTrackingSearch] = useState('');
  const [applicationsSearch, setApplicationsSearch] = useState('');
  const [programRequestsSearch, setProgramRequestsSearch] = useState('');

  const [ltSearchDebounced, setLtSearchDebounced] = useState('');
  const [appSearchDebounced, setAppSearchDebounced] = useState('');
  const [prSearchDebounced, setPrSearchDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setLtSearchDebounced(loanTrackingSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [loanTrackingSearch]);

  useEffect(() => {
    const t = setTimeout(() => setAppSearchDebounced(applicationsSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [applicationsSearch]);

  useEffect(() => {
    const t = setTimeout(() => setPrSearchDebounced(programRequestsSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [programRequestsSearch]);

  const buildLtQuery = useCallback(() => {
    const params = new URLSearchParams();
    appendParam(params, 'ltPage', ltPage);
    appendParam(params, 'ltPageSize', PAGE_SIZE);
    appendParam(params, 'ltSort', sortToParam(ltSort));
    appendParam(params, 'ltLenderId', ltLenderId);
    appendParam(params, 'ltDateFrom', ltDateFrom);
    appendParam(params, 'ltDateTo', ltDateTo);
    if (ltSearchDebounced) appendParam(params, 'ltSearch', ltSearchDebounced);
    return params.toString();
  }, [ltPage, ltSort, ltLenderId, ltDateFrom, ltDateTo, ltSearchDebounced]);

  const buildDashboardQuery = useCallback(() => {
    const params = new URLSearchParams();
    appendParam(params, 'prPage', prPage);
    appendParam(params, 'prPageSize', PAGE_SIZE);
    appendParam(params, 'prSort', sortToParam(prSort));
    appendParam(params, 'prLenderId', prLenderId);
    appendParam(params, 'prDateFrom', prDateFrom);
    appendParam(params, 'prDateTo', prDateTo);

    appendParam(params, 'appPage', appPage);
    appendParam(params, 'appPageSize', PAGE_SIZE);
    appendParam(params, 'appSort', sortToParam(appSort));
    appendParam(params, 'appLenderId', appLenderId);
    appendParam(params, 'appDateFrom', appDateFrom);
    appendParam(params, 'appDateTo', appDateTo);
    if (prSearchDebounced) appendParam(params, 'prSearch', prSearchDebounced);
    if (appSearchDebounced) appendParam(params, 'appSearch', appSearchDebounced);
    return params.toString();
  }, [
    prPage,
    prSort,
    prLenderId,
    prDateFrom,
    prDateTo,
    appPage,
    appSort,
    appLenderId,
    appDateFrom,
    appDateTo,
    prSearchDebounced,
    appSearchDebounced,
  ]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const ltRes = await api.get({
          path: `/v2/edos/dashboard-loan-tracking-assignments?${buildLtQuery()}`,
        });
        if (cancelled) return;
        const ltItems = Array.isArray(ltRes?.items) ? ltRes.items : Array.isArray(ltRes) ? ltRes : [];
        const ltTotal =
          typeof ltRes?.total === 'number'
            ? ltRes.total
            : Array.isArray(ltRes)
              ? ltRes.length
              : ltItems.length;
        setLoanTrackingEvents(ltItems);
        setLoanTrackingTotal(ltTotal);
      } catch {
        if (cancelled) return;
        setLoanTrackingEvents([]);
        setLoanTrackingTotal(0);
      } finally {
        if (!cancelled) setAssignmentsLoaded(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [buildLtQuery]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const dashboard = await api.get({ path: `/v2/edos/dashboard-data?${buildDashboardQuery()}` });
        if (cancelled) return;
        setDashboardData(dashboard || null);
      } catch {
        if (cancelled) return;
        setDashboardData(null);
      } finally {
        if (!cancelled) setDashboardLoaded(true);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [buildDashboardQuery]);

  const role = dashboardData?.role;
  const isAdmin = role === PROGRAM_ACCESS_ROLES.admin;
  const programRequestsAll = dashboardData?.programRequests ?? [];
  const programRequestsIndividual = dashboardData?.programRequestsIndividual ?? [];
  const applicationsAll = dashboardData?.applications ?? [];
  const applicationsIndividual = dashboardData?.applicationsIndividual ?? [];

  const programRequestsList =
    isAdmin || role === PROGRAM_ACCESS_ROLES.analyst ? programRequestsAll : programRequestsIndividual;

  const programRequestsTotal =
    isAdmin || role === PROGRAM_ACCESS_ROLES.analyst
      ? dashboardData?.programRequestsTotal ?? 0
      : dashboardData?.programRequestsIndividualTotal ?? 0;

  const applicationsList = isAdmin ? applicationsAll : applicationsIndividual;
  const applicationsTotal = isAdmin
    ? dashboardData?.applicationsTotal ?? 0
    : dashboardData?.applicationsIndividualTotal ?? 0;

  const filterLenders = dashboardData?.filterLenders ?? EMPTY_FILTER_LENDERS;

  const loanTrackingRows = useMemo(
    () =>
      loanTrackingEvents.map((event) => ({
        id: event.applicationId,
        businessName: event.application?.business?.name || `Application #${event.applicationId}`,
        programName: event.application?.fundingProgram?.name || '—',
        lenderLoanNumber: event.application?.lenderLoanNumber || '—',
        eventType: event.eventCode ? loanTrackingEventTypeDisplay(event.eventCode, event.status) : '—',
        status: event.status ? loanTrackingEventStatusDisplay(event.eventCode, event.status) : '—',
        action: ({ headers, colIndex }) => (
          <td key={headers[colIndex]?.key} className="text-right">
            <Link
              to={`/edo/portfolio/${event.applicationId}?tab=loanTracking`}
              className="btn btn-sm btn-outline-dark rounded"
            >
              View
            </Link>
          </td>
        ),
      })),
    [loanTrackingEvents]
  );

  const programRequestRows = useMemo(
    () => programRequestToRows(programRequestsList),
    [programRequestsList]
  );

  const applicationRows = useMemo(() => applicationToRows(applicationsList), [applicationsList]);

  const renderTable = useCallback(
    (
      headers,
      rows,
      emptyMessage,
      onClick,
      page,
      setPage,
      total,
      sort,
      setSort,
      defaultSort,
      pageSize = PAGE_SIZE
    ) => {
      if (!rows.length) return <p className="text-muted mb-0">{emptyMessage}</p>;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));
      const pagedOnClick = onClick ? (rowIndex) => onClick(rows[rowIndex]) : undefined;
      return (
        <>
          <div className="extend-table-container no-padding">
            <SimpleTable
              headers={headers}
              rows={rows}
              tableProps={{ className: 'mb-0' }}
              onClick={pagedOnClick}
              sort={sort?.key ? sort : undefined}
              onSort={(newSort) => {
                setSort(newSort ?? defaultSort);
                setPage(1);
              }}
            />
          </div>
          <DashboardTablePagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      );
    },
    []
  );

  const resetLtFilters = useCallback(() => {
    setLtLenderId('');
    setLtDateFrom('');
    setLtDateTo('');
    setLoanTrackingSearch('');
    setLtPage(1);
  }, []);
  const resetPrFilters = useCallback(() => {
    setPrLenderId('');
    setPrDateFrom('');
    setPrDateTo('');
    setProgramRequestsSearch('');
    setPrPage(1);
  }, []);
  const resetAppFilters = useCallback(() => {
    setAppLenderId('');
    setAppDateFrom('');
    setAppDateTo('');
    setApplicationsSearch('');
    setAppPage(1);
  }, []);

  const defaultLtSort = useMemo(() => ({ key: 'eventDate', dir: 'desc' }), []);
  const defaultAppPrSort = useMemo(() => ({ key: 'createdDate', dir: 'desc' }), []);
  const defaultAppSort = useMemo(() => ({ key: 'id', dir: 'desc' }), []);

  const loanTrackingTitle = useMemo(
    () => (
      <div className="text-primary mb-0 font-weight-bold">
        Your Loan Tracking Assignments ({assignmentsLoaded ? loanTrackingTotal : 0})
      </div>
    ),
    [assignmentsLoaded, loanTrackingTotal]
  );

  const applicationsTitle = useMemo(
    () => (
      <div className="d-flex flex-column flex-lg-row flex-lg-wrap align-items-lg-baseline">
        <div className="text-primary mb-0 font-weight-bold mb-8 mb-lg-0 mr-lg-8">
          Applications in Progress ({dashboardLoaded ? applicationsTotal : '—'})
        </div>
        <span className="small text-muted font-weight-normal">Non-Approved, latest Loan Tracking event shown</span>
      </div>
    ),
    [dashboardLoaded, applicationsTotal]
  );

  const programRequestsTitle = useMemo(
    () => (
      <div className="text-primary mb-0 font-weight-bold">
        Pending Program Requests ({dashboardLoaded ? programRequestsTotal : '—'})
      </div>
    ),
    [dashboardLoaded, programRequestsTotal]
  );

  const loanTrackingSectionChildren = useMemo(() => {
    if (!assignmentsLoaded) return <Loader className="py-24" message="Loading…" />;
    return renderTable(
      LOAN_TRACKING_HEADERS,
      loanTrackingRows,
      'You have no Loan Tracking assignments pending approval.',
      (row) => history.push(`/edo/portfolio/${row.id}?tab=loanTracking`),
      ltPage,
      setLtPage,
      loanTrackingTotal,
      ltSort,
      setLtSort,
      defaultLtSort
    );
  }, [
    assignmentsLoaded,
    defaultLtSort,
    history,
    loanTrackingRows,
    loanTrackingTotal,
    ltPage,
    ltSort,
    renderTable,
  ]);

  const applicationsSectionChildren = useMemo(() => {
    if (!dashboardLoaded) return <Loader className="py-24" message="Loading…" />;
    return renderTable(
      APPLICATION_HEADERS,
      applicationRows,
      'No non-approved applications.',
      (row) => history.push(`/edo/portfolio/${row.id}?tab=loanTracking`),
      appPage,
      setAppPage,
      applicationsTotal,
      appSort,
      setAppSort,
      defaultAppSort
    );
  }, [
    applicationRows,
    applicationsTotal,
    appPage,
    appSort,
    dashboardLoaded,
    defaultAppSort,
    history,
    renderTable,
  ]);

  const programRequestsSectionChildren = useMemo(() => {
    if (!dashboardLoaded) return <Loader className="py-24" message="Loading…" />;
    return renderTable(
      PROGRAM_REQUEST_HEADERS,
      programRequestRows,
      'No pending program requests.',
      (row) => history.push(`/edo/program-requests/${row.id}`),
      prPage,
      setPrPage,
      programRequestsTotal,
      prSort,
      setPrSort,
      defaultAppPrSort
    );
  }, [
    dashboardLoaded,
    defaultAppPrSort,
    history,
    prPage,
    prSort,
    programRequestRows,
    programRequestsTotal,
    renderTable,
  ]);

  useEffect(() => {
    setLtPage(1);
  }, [ltLenderId, ltDateFrom, ltDateTo, ltSearchDebounced]);

  useEffect(() => {
    setPrPage(1);
  }, [prLenderId, prDateFrom, prDateTo, prSearchDebounced]);

  useEffect(() => {
    setAppPage(1);
  }, [appLenderId, appDateFrom, appDateTo, appSearchDebounced]);

  return (
    <div>
      <div className="edo-dashboard-overview bg-white p-16 mb-16 rounded-0">
        {mainSectionOrderDisplay.map((mainId) => (
          <div
            key={mainId}
            onDragOver={canReorderMainSections ? handleMainSectionDragOver : undefined}
            onDrop={canReorderMainSections ? (ev) => handleMainSectionDrop(ev, mainId) : undefined}
          >
            {mainId === 'trackingRequests' && (
              <EdoMainSectionCard
                sectionDragId="trackingRequests"
                title="Tracking & Requests"
                expanded={mainBlockExpanded.trackingRequests}
                onToggle={() => toggleMainBlockExpanded('trackingRequests')}
                canReorderMain={canReorderMainSections}
              >
                  {sectionOrder.map((sectionId) => (
                    <div
                      key={sectionId}
                      onDragOver={canReorderSections ? handleSectionDragOver : undefined}
                      onDrop={canReorderSections ? (ev) => handleSectionDrop(ev, sectionId) : undefined}
                    >
                      {sectionId === 'loanTracking' && (
                <MemoDashboardTableSection
                title={loanTrackingTitle}
                filterLenders={filterLenders}
                lenderValue={ltLenderId}
                onLenderChange={setLtLenderId}
                dateFrom={ltDateFrom}
                dateTo={ltDateTo}
                onDateFromChange={setLtDateFrom}
                onDateToChange={setLtDateTo}
                searchValue={loanTrackingSearch}
                onSearchChange={setLoanTrackingSearch}
                searchDisabled={false}
                onClearFilters={resetLtFilters}
                idPrefix="edo-dash-lt"
                defaultExpanded
                savedExpanded={trackingCardsPrefs.loanTrackingExpanded}
                persistExpanded={persistEdoDashboardCards}
                preferenceKey="loanTrackingExpanded"
                preferencesSyncRef={cardPreferencesSyncRef}
                headerDragHandle={
                  <ReorderDragHandle
                    itemId="loanTracking"
                    enabled={canReorderSections}
                    dataTransferType={DASHBOARD_SECTION_DRAG_TYPE}
                    variant="primary"
                    ariaLabel="Drag to reorder section"
                  />
                }
                >
                  {loanTrackingSectionChildren}
                </MemoDashboardTableSection>
              )}
              {sectionId === 'applications' && !hideAppProgramSections && (
                <MemoDashboardTableSection
                defaultExpanded={false}
                title={applicationsTitle}
                filterLenders={filterLenders}
                lenderValue={appLenderId}
                onLenderChange={setAppLenderId}
                dateFrom={appDateFrom}
                dateTo={appDateTo}
                onDateFromChange={setAppDateFrom}
                onDateToChange={setAppDateTo}
                searchValue={applicationsSearch}
                onSearchChange={setApplicationsSearch}
                searchDisabled={!dashboardLoaded}
                onClearFilters={resetAppFilters}
                idPrefix="edo-dash-app"
                savedExpanded={trackingCardsPrefs.applicationsExpanded}
                persistExpanded={persistEdoDashboardCards}
                preferenceKey="applicationsExpanded"
                preferencesSyncRef={cardPreferencesSyncRef}
                headerDragHandle={
                  <ReorderDragHandle
                    itemId="applications"
                    enabled={canReorderSections}
                    dataTransferType={DASHBOARD_SECTION_DRAG_TYPE}
                    variant="primary"
                    ariaLabel="Drag to reorder section"
                  />
                }
                >
                  {applicationsSectionChildren}
                </MemoDashboardTableSection>
              )}
              {sectionId === 'programRequests' && !hideAppProgramSections && (
                <MemoDashboardTableSection
                defaultExpanded={false}
                title={programRequestsTitle}
                filterLenders={filterLenders}
                lenderValue={prLenderId}
                onLenderChange={setPrLenderId}
                dateFrom={prDateFrom}
                dateTo={prDateTo}
                onDateFromChange={setPrDateFrom}
                onDateToChange={setPrDateTo}
                searchValue={programRequestsSearch}
                onSearchChange={setProgramRequestsSearch}
                searchDisabled={!dashboardLoaded}
                onClearFilters={resetPrFilters}
                idPrefix="edo-dash-pr"
                savedExpanded={trackingCardsPrefs.programRequestsExpanded}
                persistExpanded={persistEdoDashboardCards}
                preferenceKey="programRequestsExpanded"
                preferencesSyncRef={cardPreferencesSyncRef}
                headerDragHandle={
                  <ReorderDragHandle
                    itemId="programRequests"
                    enabled={canReorderSections}
                    dataTransferType={DASHBOARD_SECTION_DRAG_TYPE}
                    variant="primary"
                    ariaLabel="Drag to reorder section"
                  />
                }
                >
                  {programRequestsSectionChildren}
                </MemoDashboardTableSection>
              )}
                    </div>
                  ))}
              </EdoMainSectionCard>
            )}
            {mainId === 'loanProcessingBatches' && showLoanBatchesSection && (
              <EdoMainSectionCard
                sectionDragId="loanProcessingBatches"
                title="Loan Batch Processing"
                expanded={mainBlockExpanded.loanProcessingBatches}
                onToggle={() => toggleMainBlockExpanded('loanProcessingBatches')}
                canReorderMain={canReorderMainSections}
              >
                  {loanBatchSectionOrder.map((bid) => {
                    const meta = LOAN_BATCH_QUEUE_META[bid];
                    return (
                      <div
                        key={bid}
                        onDragOver={canReorderLoanBatchSections ? handleLoanBatchSectionDragOver : undefined}
                        onDrop={
                          canReorderLoanBatchSections
                            ? (ev) => handleLoanBatchSectionDrop(ev, bid)
                            : undefined
                        }
                      >
                        <LoanProcessingBatchesSection
                          title={meta.title}
                          queue={meta.queue}
                          status={meta.status}
                          emptyText={meta.emptyText}
                          userData={dashboardUserData}
                          defaultExpanded
                          savedExpanded={loanBatchCardsPrefs[meta.preferenceKey]}
                          persistExpanded={persistEdoDashboardCards}
                          preferenceKey={meta.preferenceKey}
                          preferencesSyncRef={cardPreferencesSyncRef}
                          panelAside={
                            bid === 'completed' && showManageBatchProcessingLink ? (
                              <Link
                                to="/edo/manage-batch-processing"
                                className="btn btn-sm btn-outline-primary text-nowrap"
                              >
                                Manage Batch Processing
                              </Link>
                            ) : null
                          }
                          headerDragHandle={
                            <ReorderDragHandle
                              itemId={bid}
                              enabled={canReorderLoanBatchSections}
                              dataTransferType={LOAN_BATCH_SECTION_DRAG_TYPE}
                              variant="primary"
                              ariaLabel="Drag to reorder section"
                            />
                          }
                        />
                      </div>
                    );
                  })}
              </EdoMainSectionCard>
            )}
            {mainId === 'quickAccess' && (
              <EdoMainSectionCard
                sectionDragId="quickAccess"
                title="Quick Access"
                expanded={mainBlockExpanded.quickAccess}
                onToggle={() => toggleMainBlockExpanded('quickAccess')}
                canReorderMain={canReorderMainSections}
                bodyClassName="px-16 py-16 edo-dashboard-main-card-body--quick-access"
              >
                  <QuickAccessGrid
                    items={quickAccessItemsOrdered}
                    canReorderQuickAccess={canReorderQuickAccess}
                    onQuickAccessDragOver={handleQuickAccessDragOver}
                    onQuickAccessDrop={handleQuickAccessDrop}
                    suppressHeading
                  />
              </EdoMainSectionCard>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EdoDashboardOverview;
